import { motion, AnimatePresence } from "framer-motion";
import { Share2, Copy, RotateCcw, PartyPopper, Sparkles, Loader2, MessageCircleReply, ScanFace, Radar, Target } from "lucide-react";
import { BrutalistProgressBar } from "./BrutalistProgressBar";
import { useLang } from "../i18n";

const VCOLORS = {
  MANDA: { emoji: "🟢", bg: "#00FFA3" },
  ESPERA: { emoji: "🟡", bg: "#FFD500" },
  NAO_MANDA: { emoji: "🔴", bg: "#FF3366" },
};

export const VerdictCard = ({ result, noFilter, mode = "send", onShare, onCopy, onAnother, onReply3, reply3ing, replies, onJudgeMe, judging, observations, onCopyText }) => {
  const { t } = useLang();
  const v = VCOLORS[result.verdict] || VCOLORS.ESPERA;
  const labels = mode === "received" ? t.v_received : t.v_send;
  const label = labels[result.verdict] || labels.ESPERA;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: "spring", stiffness: 120, damping: 16 }}
      className="w-full"
    >
      <div
        data-testid="verdict-card"
        className="bg-white border-[4px] sm:border-[6px] border-black rounded-2xl shadow-brutal-lg overflow-hidden"
      >
        {/* Category + mode ribbon */}
        <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 border-b-[3px] border-black bg-[#F4F0E6]">
          <span
            data-testid="category-chip"
            className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold uppercase tracking-[0.06em] bg-white border-[2px] border-black rounded-full px-3 py-1"
          >
            <span className="text-base">{result.category?.emoji}</span>
            {result.category?.label}
          </span>
          {noFilter && (
            <span className="text-xs font-bold uppercase tracking-[0.06em] text-[#FF3366]">
              {t.no_filter_ribbon}
            </span>
          )}
        </div>

        {/* Verdict badge */}
        <div className="px-4 sm:px-6 pt-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#444] mb-2">{t.vc_verdict}</p>
          <div
            data-testid="verdict-badge"
            className="w-full border-[3px] border-black rounded-xl shadow-brutal py-4 sm:py-5 px-4 text-center"
            style={{ backgroundColor: v.bg }}
          >
            <div className="text-4xl sm:text-5xl font-display font-bold uppercase tracking-tighter leading-none flex items-center justify-center gap-3">
              <span>{v.emoji}</span>
              <span>{label}</span>
            </div>
          </div>
        </div>

        {/* Quick read (Priority 1: read_label + probabilistic score) */}
        {(result.read_label || result.read_score) && (
          <div className="px-4 sm:px-6 pt-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#444] mb-2 flex items-center gap-1.5">
              <Radar size={14} strokeWidth={2.5} /> {t.vc_read}
            </p>
            {result.read_label && (
              <span
                data-testid="read-label"
                className="inline-block bg-black text-white border-[3px] border-black rounded-lg px-3 py-1.5 text-sm sm:text-base font-display font-bold uppercase tracking-tight shadow-brutal"
              >
                {result.read_label}
              </span>
            )}
            {result.read_score && typeof result.read_score.value === "number" && (
              <div className="mt-3" data-testid="read-score">
                <BrutalistProgressBar
                  testId="read-score-bar"
                  label={`${result.read_score.label} · ${result.read_score.value}/100`}
                  value={result.read_score.value}
                  color="purple"
                  delay={0.1}
                />
              </div>
            )}
          </div>
        )}

        {/* AI comment */}
        <div className="px-4 sm:px-6 pt-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#444] mb-2">{t.vc_truth}</p>
          <p data-testid="ai-comment" className="text-lg sm:text-xl font-semibold leading-snug">
            "{result.comment}"
          </p>
        </div>

        {/* WHAT TO DO NOW (action) */}
        {result.action && (
          <div className="px-4 sm:px-6 pt-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#444] mb-2 flex items-center gap-1.5">
              <Target size={14} strokeWidth={2.5} /> {t.vc_action}
            </p>
            <div
              data-testid="action-block"
              className="border-[3px] border-black rounded-xl bg-[#00FFA3] px-4 py-3 shadow-brutal"
            >
              <p className="text-base sm:text-lg font-bold leading-snug text-[#0a3b2b]">{result.action}</p>
            </div>
          </div>
        )}

        {/* Subtext */}
        <div className="px-4 sm:px-6 pt-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#444] mb-2">
            {t.vc_going}
          </p>
          <p data-testid="ai-subtext" className="text-base sm:text-lg font-medium text-[#222] leading-relaxed">
            {result.subtext}
          </p>
        </div>

        {/* Alternativas (SOMENTE no modo "send" — reescrever a própria mensagem) */}
        {mode === "send" && result.alternatives && result.alternatives.length > 0 && (
          <div className="px-4 sm:px-6 pt-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#444] mb-2">
              {t.vc_alts}
            </p>
            <div className="flex flex-col gap-2" data-testid="alternatives">
              {result.alternatives.map((alt, i) => (
                <div key={i} className="border-[2px] border-black rounded-xl bg-[#F4F0E6] p-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#BD00FF] mb-1">
                    {alt.label}
                  </p>
                  <p className="text-base font-semibold leading-snug">"{alt.text}"</p>
                  <button
                    data-testid={`copy-alt-${i}`}
                    onClick={() => onCopyText(alt.text)}
                    className="mt-2 inline-flex items-center gap-1.5 bg-[#FFD500] border-[2px] border-black rounded-lg px-2.5 py-1.5 text-xs font-bold uppercase tracking-[0.04em] shadow-[3px_3px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[1px_1px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]"
                  >
                    <Copy size={14} strokeWidth={2.5} /> {t.sug_copy}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Extras do modo DEEP (crédito pago) */}
        {result.bond_read && (
          <div className="px-4 sm:px-6 pt-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#BD00FF] mb-2">{t.vc_bond}</p>
            <p data-testid="bond-read" className="text-base sm:text-lg font-medium text-[#222] leading-relaxed">
              {result.bond_read}
            </p>
          </div>
        )}
        {result.intention && (
          <div className="px-4 sm:px-6 pt-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#444] mb-2">{t.vc_intention}</p>
            <p data-testid="intention" className="text-base font-medium text-[#222] leading-relaxed">{result.intention}</p>
          </div>
        )}
        {result.risks && (
          <div className="px-4 sm:px-6 pt-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3366] mb-2">{t.vc_risks}</p>
            <p data-testid="risks" className="text-base font-medium text-[#222] leading-relaxed">{result.risks}</p>
          </div>
        )}
        {result.reply_options && result.reply_options.length > 0 && (
          <div className="px-4 sm:px-6 pt-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#444] mb-2">{t.vc_replies}</p>
            <div className="flex flex-col gap-2" data-testid="reply-options">
              {result.reply_options.map((r, i) => (
                <div key={i} className="border-[2px] border-black rounded-xl bg-[#00FFA3] p-3">
                  <p className="text-base font-semibold leading-snug">"{r}"</p>
                  <button
                    data-testid={`copy-reply-${i}`}
                    onClick={() => onCopyText(r)}
                    className="mt-2 inline-flex items-center gap-1.5 bg-white border-[2px] border-black rounded-lg px-2.5 py-1.5 text-xs font-bold uppercase tracking-[0.04em] shadow-[3px_3px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[1px_1px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]"
                  >
                    <Copy size={14} strokeWidth={2.5} /> {t.sug_copy}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {result.depth === "summary" && (
          <div className="px-4 sm:px-6 pt-4">
            <p data-testid="summary-note" className="text-xs font-semibold text-[#666] leading-relaxed">
              {t.vc_summary_note}
            </p>
          </div>
        )}

        {/* Scores — exibidos apenas no modo SEM FILTRO */}
        {noFilter && (
        <div className="px-4 sm:px-6 pt-6 flex flex-col gap-4">
          <BrutalistProgressBar
            testId="score-regret"
            label={t.sc_regret}
            value={result.scores.regret}
            color="red"
            delay={0.1}
          />
          <BrutalistProgressBar
            testId="score-desperation"
            label={t.sc_desp}
            value={result.scores.desperation}
            color="purple"
            delay={0.25}
          />
          <BrutalistProgressBar
            testId="score-phone"
            label={t.sc_phone}
            value={result.scores.phone_check}
            color="blue"
            delay={0.4}
          />
        </div>
        )}

        {/* Final verdict */}
        <div className="px-4 sm:px-6 pt-6 pb-6">
          <div className="border-[3px] border-black rounded-xl bg-black text-white px-4 py-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#00FFA3] mb-1">
              {t.vc_final}
            </p>
            <p data-testid="final-verdict" className="text-lg sm:text-xl font-display font-semibold leading-tight">
              {result.final_verdict}
            </p>
          </div>
          <p className="text-center text-xs font-bold uppercase tracking-[0.15em] text-[#444] mt-3">
            mandoounaomando · {t.tagline}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <BrutalBtn testId="share-btn" onClick={onShare} bg="#00FFA3">
          <Share2 size={18} strokeWidth={2.5} /> {t.vc_share}
        </BrutalBtn>
        <BrutalBtn testId="copy-btn" onClick={onCopy} bg="#FFD500">
          <Copy size={18} strokeWidth={2.5} /> {t.vc_copy}
        </BrutalBtn>
      </div>
      <BrutalBtn testId="another-btn" onClick={onAnother} bg="#FFFFFF" full>
        <RotateCcw size={18} strokeWidth={2.5} /> {t.vc_another}
      </BrutalBtn>

      {/* Reply Mode (Priority 2: 3 replies) */}
      <div className="mt-6 border-[3px] border-black rounded-2xl bg-[#00FFA3] shadow-brutal p-4 sm:p-5">
        <p className="font-display font-semibold text-lg uppercase tracking-tight flex items-center gap-2 mb-1">
          <MessageCircleReply size={20} strokeWidth={2.5} /> {t.reply3_title}
        </p>
        <p className="font-medium text-sm text-[#0a3b2b] mb-4">{t.reply3_sub}</p>
        <button
          data-testid="reply3-btn"
          onClick={onReply3}
          disabled={reply3ing}
          className="w-full inline-flex items-center justify-center gap-2 bg-black text-white border-[3px] border-black rounded-xl px-4 py-3 font-bold uppercase tracking-[0.04em] text-sm sm:text-base shadow-brutal transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[4px] hover:translate-y-[4px] active:shadow-none active:translate-x-[6px] active:translate-y-[6px] disabled:opacity-70"
        >
          {reply3ing ? (
            <><Loader2 size={18} strokeWidth={2.5} className="animate-spin" /> {t.reply3_loading}</>
          ) : (
            <><Sparkles size={18} strokeWidth={2.5} /> {t.reply3_btn}</>
          )}
        </button>

        <AnimatePresence>
          {replies && replies.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-4 flex flex-col gap-3" data-testid="replies-list">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0a3b2b]">{t.reply3_res}</p>
                {replies.map((r, i) => (
                  <div key={i} className="bg-white border-[3px] border-black rounded-xl p-3">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#BD00FF] mb-1">
                      {t.reply3_tones[r.tone] || r.tone}
                    </p>
                    <p className="text-base font-semibold leading-snug">"{r.text}"</p>
                    <button
                      data-testid={`copy-reply3-${i}`}
                      onClick={() => onCopyText(r.text)}
                      className="mt-2 inline-flex items-center gap-1.5 bg-[#FFD500] border-[2px] border-black rounded-lg px-2.5 py-1.5 text-xs font-bold uppercase tracking-[0.04em] shadow-[3px_3px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[1px_1px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px]"
                    >
                      <Copy size={14} strokeWidth={2.5} /> {t.sug_copy}
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Judge Me Too (Priority 3) */}
      <div className="mt-6 border-[3px] border-black rounded-2xl bg-[#FFD500] shadow-brutal p-4 sm:p-5">
        <p className="font-display font-semibold text-lg uppercase tracking-tight flex items-center gap-2 mb-1">
          <ScanFace size={20} strokeWidth={2.5} /> {t.judgeme_title}
        </p>
        <p className="font-medium text-sm text-[#3d3200] mb-4">{t.judgeme_sub}</p>
        <button
          data-testid="judgeme-btn"
          onClick={onJudgeMe}
          disabled={judging}
          className="w-full inline-flex items-center justify-center gap-2 bg-black text-white border-[3px] border-black rounded-xl px-4 py-3 font-bold uppercase tracking-[0.04em] text-sm sm:text-base shadow-brutal transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[4px] hover:translate-y-[4px] active:shadow-none active:translate-x-[6px] active:translate-y-[6px] disabled:opacity-70"
        >
          {judging ? (
            <><Loader2 size={18} strokeWidth={2.5} className="animate-spin" /> {t.judgeme_loading}</>
          ) : (
            <><ScanFace size={18} strokeWidth={2.5} /> {t.judgeme_btn}</>
          )}
        </button>

        <AnimatePresence>
          {observations && observations.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="mt-4 bg-white border-[3px] border-black rounded-xl p-4" data-testid="observations-list">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#444] mb-2">{t.judgeme_res}</p>
                <ul className="flex flex-col gap-2">
                  {observations.map((o, i) => (
                    <li key={i} className="flex gap-2 text-base font-medium text-[#222] leading-snug">
                      <span className="text-[#FFD500] font-bold" style={{ WebkitTextStroke: "1px #000" }}>★</span>
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Viral prompt */}
      <div className="mt-6 border-[3px] border-black rounded-2xl bg-[#BD00FF] shadow-brutal px-4 py-5 text-center">
        <p className="text-white font-display font-semibold text-xl uppercase tracking-tight flex items-center justify-center gap-2">
          <PartyPopper size={22} strokeWidth={2.5} /> {t.vc_viral}
        </p>
        <BrutalBtn testId="send-friend-btn" onClick={onShare} bg="#FFFFFF" full>
          {t.vc_send_friend}
        </BrutalBtn>
      </div>
    </motion.div>
  );
};

const BrutalBtn = ({ children, onClick, bg, full, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    style={{ backgroundColor: bg }}
    className={`${full ? "w-full mt-3" : ""} inline-flex items-center justify-center gap-2 border-[3px] border-black rounded-xl px-4 py-3 font-bold uppercase tracking-[0.04em] text-sm sm:text-base shadow-brutal transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[4px] hover:translate-y-[4px] active:shadow-none active:translate-x-[6px] active:translate-y-[6px] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#BD00FF]`}
  >
    {children}
  </button>
);
